import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Modal } from "react-bootstrap";
import TicketOptionCard from "./TicketOptionCard";
import { AutoComplete } from "antd";

const EVENT_DEFS = [
  { key: "ticket", name: "accompanying_ticket", label: "Accompanying Person Ticket" },
  { key: "opening", name: "accompanying_opening", label: "Opening Ceremony" },
  { key: "reception", name: "accompanying_reception", label: "Welcome Reception" },
  { key: "gala", name: "accompanying_gala", label: "Congress Gala Dinner" },
];

const EVENT_DEF_BY_NAME = EVENT_DEFS.reduce((acc, def) => {
  acc[def.name] = def;
  return acc;
}, {});

const PHONE_REGEX = /^[0-9+\-\s()]+$/;
const IC_MY_REGEX = /^[0-9\-]+$/;
const IC_INTL_REGEX = /^[a-zA-Z0-9\-]+$/;
const INTL_TEL_INPUT_STYLE_ID = "intl-tel-input-padding";

const isMalaysia = (country) => {
  const value = (country || "").toString().trim();
  return value === "MY" || value === "Malaysia";
};

const phoneError = (value, label) => {
  if (!value) return `${label} is required.`;
  if (!PHONE_REGEX.test(value)) {
    return `${label} must contain only digits, spaces, +, -, or parentheses.`;
  }
  return null;
};

const icNumberError = (value, country) => {
  if (!value) return "IC / Passport number is required.";
  if (isMalaysia(country) && !IC_MY_REGEX.test(value)) {
    return "IC number must contain digits and hyphens only for Malaysia.";
  }
  if (!isMalaysia(country) && !IC_INTL_REGEX.test(value)) {
    return "IC / Passport number must contain only letters, digits, and hyphens.";
  }
  return null;
};

const ensureIntlTelInputStyles = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById(INTL_TEL_INPUT_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = INTL_TEL_INPUT_STYLE_ID;
  style.innerHTML = `
    .iti { width: 100%; }
    .iti__tel-input { padding-left: 5.5rem !important; width: 100%; }
  `;
  document.head.appendChild(style);
};

export const validateAccompanyingPersonPayload = (person) => {
  const user = person?.user || {};
  const errors = {
    phone: phoneError(user.phone, "Phone"),
    emergencyContactPhone: phoneError(
      user.emergency_contact_phone,
      "Emergency contact phone"
    ),
    icNumber: icNumberError(user.ic_number, user.country),
  };

  return Object.entries(errors).reduce((acc, [key, value]) => {
    if (value) acc[key] = value;
    return acc;
  }, {});
};

const formatMoney = (cents, currency) => {
  if (cents === null || cents === undefined) return "-";
  const ccy = (currency || "myr").toString().toUpperCase();
  const amount = Number(cents) / 100;
  return `${ccy} ${amount.toFixed(2)}`;
};

const buildEventsByKey = (events = []) => {
  const map = {};
  events.forEach((event) => {
    const def = EVENT_DEF_BY_NAME[event?.name];
    if (!def) return;
    map[def.key] = event;
  });
  return map;
};

const eventDisplayName = (event, def) => event?.title || def.label;

const buildDefaultForm = (defaultCountry) => ({
  email: "",
  title: "",
  fullName: "",
  badgeName: "",
  gender: "",
  country: defaultCountry || "",
  icNumber: "",
  phone: "",
  mealPreference: "",
  mealPreferenceOther: "",
  knowAbout: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
});

const buildFormFromPerson = (person, defaultCountry) => ({
  email: person?.user?.email || "",
  title: person?.user?.title || "",
  fullName: person?.user?.full_name || "",
  badgeName: person?.user?.badge_name || "",
  gender: person?.user?.gender || "",
  country: person?.user?.country || defaultCountry || "",
  icNumber: person?.user?.ic_number || "",
  phone: person?.user?.phone || "",
  mealPreference: person?.user?.meal_preference || "",
  mealPreferenceOther: person?.user?.meal_preference_other || "",
  knowAbout: person?.user?.know_about || "",
  emergencyContactName: person?.user?.emergency_contact_name || "",
  emergencyContactPhone: person?.user?.emergency_contact_phone || "",
});

const AccompanyingPersonCard = ({
  currency,
  events,
  maxCount,
  existingCount,
  canAdd,
  persons,
  onOpenModal,
  onEditPerson,
  onRemovePerson,
}) => {
  const eventsByKey = useMemo(() => buildEventsByKey(events), [events]);
  const eventRows = EVENT_DEFS.map((def) => {
    const event = eventsByKey[def.key];
    if (!event) return null;
    return { def, event };
  }).filter(Boolean);

  const remainingSlots = Math.max(
    0,
    (maxCount || 0) - (existingCount || 0) - (persons?.length || 0)
  );
  const actionDisabled = !canAdd || remainingSlots <= 0;
  const hasEventRows = eventRows.length > 0;
  const cheapestEvent = eventRows.reduce((cheapest, current) => {
    if (!cheapest) return current;
    if ((current.event.priceCents || 0) < (cheapest.event.priceCents || 0)) {
      return current;
    }
    return cheapest;
  }, null);

  return (
    <TicketOptionCard
      title="Accompanying Person Ticket"
      badges={[`Max ${maxCount || 3} pax`]}
      description={
        <>
          {!canAdd && hasEventRows && (
            <div className="mt-2 text-danger">
              Requires an active 3-Day Congress Pass to add accompanying persons.
            </div>
          )}
          {canAdd && remainingSlots === 0 && (
            <div className="mt-2 text-danger">
              You have reached the maximum number of accompanying persons.
            </div>
          )}
        </>
      }
      extraContent={
        (persons || []).length > 0 && (
          <ul className="mt-3 list-group">
            {(persons || []).map((person) => {
              const name =
                person?.user?.full_name || person?.user?.email || "Accompanying Person";
              const selectedLabels = EVENT_DEFS.filter(
                (def) => person?.events?.[def.key]
              ).map((def) => {
                const event = eventsByKey[def.key];
                return eventDisplayName(event, def);
              });

              let totalCents = 0;
              let totalCurrency = currency;
              let hasPrice = false;

              EVENT_DEFS.forEach((def) => {
                if (!person?.events?.[def.key]) return;
                const event = eventsByKey[def.key];
                if (!event) return;
                totalCents += Number(event.priceCents || 0);
                totalCurrency = event.currency || totalCurrency;
                hasPrice = true;
              });

              return (
                <li key={person.id} className="list-group-item">
                  <div className="gap-3 d-flex justify-content-between">
                    <div>
                      <div className="fw-bold">{name}</div>
                      <div className="small text-muted">
                        Events: {selectedLabels.length > 0 ? selectedLabels.join(", ") : "-"}
                      </div>
                    </div>
                    <div className="flex items-center flex-shrink-0 gap-2">
                      <div className="fw-bold">
                        {hasPrice ? formatMoney(totalCents, totalCurrency) : "-"}
                      </div>
                      {onEditPerson && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => onEditPerson(person)}
                        >
                          Edit
                        </Button>
                      )}
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => onRemovePerson(person.id)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )
      }
      // priceLabel={cheapestEvent ? `From: ${formatMoney(cheapestEvent.event.priceCents, cheapestEvent.event.currency)}` : "Varies"}
      soldOut={false}
      isInCart={false}
      actionDisabled={actionDisabled}
      onAction={onOpenModal}
    />
  );
};

const AccompanyingPersonModal = ({
  show,
  onHide,
  currency,
  events,
  registrationOptions,
  defaultCountry,
  onSavePerson,
  initialPerson,
}) => {
  const [form, setForm] = useState(buildDefaultForm(defaultCountry));
  const [selectedEvents, setSelectedEvents] = useState({
    ticket: false,
    opening: false,
    reception: false,
    gala: false,
  });
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const phoneInputRef = useRef(null);
  const phoneItiRef = useRef(null);
  const pendingPhoneRef = useRef(null);
  const emergencyPhoneInputRef = useRef(null);
  const emergencyPhoneItiRef = useRef(null);
  const pendingEmergencyPhoneRef = useRef(null);

  const countries = registrationOptions?.countries || [];
  const titles = registrationOptions?.titles || [];
  const genders = registrationOptions?.genders || [];
  const knowAbouts = registrationOptions?.knowAbouts || [];
  const mealPreferences = registrationOptions?.mealPreferences || [];

  const eventOptions = useMemo(() => {
    const eventsByKey = buildEventsByKey(events);
    return EVENT_DEFS.map((def) => {
      const event = eventsByKey[def.key];
      if (!event) return null;
      return {
        key: def.key,
        label: eventDisplayName(event, def),
        priceCents: event.priceCents,
        currency: event.currency,
        soldOut: event.soldOut,
      };
    }).filter(Boolean);
  }, [events]);

  useEffect(() => {
    if (!show) return;
    const nextForm = initialPerson
      ? buildFormFromPerson(initialPerson, defaultCountry)
      : buildDefaultForm(defaultCountry);

    setSelectedEvents({
      ticket: Boolean(initialPerson?.events?.ticket),
      opening: Boolean(initialPerson?.events?.opening),
      reception: Boolean(initialPerson?.events?.reception),
      gala: Boolean(initialPerson?.events?.gala),
    });
    setFieldErrors({});
    setError(null);

    let resolvedForm = nextForm;
    if (phoneItiRef.current && typeof phoneItiRef.current.setNumber === "function") {
      phoneItiRef.current.setNumber(nextForm.phone || "");
      if (phoneInputRef.current) {
        resolvedForm = { ...nextForm, phone: phoneInputRef.current.value };
      }
    } else {
      pendingPhoneRef.current = nextForm.phone || "";
      if (phoneInputRef.current) {
        phoneInputRef.current.value = nextForm.phone || "";
      }
    }
    if (
      emergencyPhoneItiRef.current &&
      typeof emergencyPhoneItiRef.current.setNumber === "function"
    ) {
      emergencyPhoneItiRef.current.setNumber(nextForm.emergencyContactPhone || "");
      if (emergencyPhoneInputRef.current) {
        resolvedForm = {
          ...resolvedForm,
          emergencyContactPhone: emergencyPhoneInputRef.current.value,
        };
      }
    } else {
      pendingEmergencyPhoneRef.current = nextForm.emergencyContactPhone || "";
      if (emergencyPhoneInputRef.current) {
        emergencyPhoneInputRef.current.value = nextForm.emergencyContactPhone || "";
      }
    }
    setForm(resolvedForm);
  }, [show, initialPerson, defaultCountry]);

  useEffect(() => {
    if (!show) {
      pendingPhoneRef.current = null;
      return undefined;
    }

    const input = phoneInputRef.current;
    if (!input || typeof window === "undefined") return undefined;
    if (typeof window.intlTelInput !== "function") return undefined;

    ensureIntlTelInputStyles();

    if (!phoneItiRef.current) {
      phoneItiRef.current = window.intlTelInput(input, {
        initialCountry: "my",
        separateDialCode: true,
        autoPlaceholder: "polite",
        preferredCountries: ["my", "sg"],
        excludeCountries: ["il", "kp"],
        nationalMode: false,
        loadUtils: () =>
          import(
            /* webpackIgnore: true */
            "https://cdn.jsdelivr.net/npm/intl-tel-input@25.10.10/build/js/utils.js"
          ),
      });
    }

    if (pendingPhoneRef.current !== null) {
      if (phoneItiRef.current && typeof phoneItiRef.current.setNumber === "function") {
        phoneItiRef.current.setNumber(pendingPhoneRef.current || "");
        if (phoneInputRef.current) {
          setForm((prev) => ({ ...prev, phone: phoneInputRef.current.value }));
        }
      }
      pendingPhoneRef.current = null;
    }

    return () => {
      if (phoneItiRef.current) {
        phoneItiRef.current.destroy();
        phoneItiRef.current = null;
      }
    };
  }, [show]);

  useEffect(() => {
    if (!show) {
      pendingEmergencyPhoneRef.current = null;
      return undefined;
    }

    const input = emergencyPhoneInputRef.current;
    if (!input || typeof window === "undefined") return undefined;
    if (typeof window.intlTelInput !== "function") return undefined;

    ensureIntlTelInputStyles();

    if (!emergencyPhoneItiRef.current) {
      emergencyPhoneItiRef.current = window.intlTelInput(input, {
        initialCountry: "my",
        separateDialCode: true,
        autoPlaceholder: "polite",
        preferredCountries: ["my", "sg"],
        excludeCountries: ["il", "kp"],
        nationalMode: false,
        loadUtils: () =>
          import(
            /* webpackIgnore: true */
            "https://cdn.jsdelivr.net/npm/intl-tel-input@25.10.10/build/js/utils.js"
          ),
      });
    }

    if (pendingEmergencyPhoneRef.current !== null) {
      if (
        emergencyPhoneItiRef.current &&
        typeof emergencyPhoneItiRef.current.setNumber === "function"
      ) {
        emergencyPhoneItiRef.current.setNumber(pendingEmergencyPhoneRef.current || "");
        if (emergencyPhoneInputRef.current) {
          setForm((prev) => ({
            ...prev,
            emergencyContactPhone: emergencyPhoneInputRef.current.value,
          }));
        }
      }
      pendingEmergencyPhoneRef.current = null;
    }

    return () => {
      if (emergencyPhoneItiRef.current) {
        emergencyPhoneItiRef.current.destroy();
        emergencyPhoneItiRef.current = null;
      }
    };
  }, [show]);

  const selectedEventLabels = useMemo(() => {
    return eventOptions
      .filter((option) => selectedEvents[option.key])
      .map((option) => option.label);
  }, [eventOptions, selectedEvents]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => ({ ...prev, [key]: null }));
    }
  };

  const closeModal = () => {
    setError(null);
    onHide();
  };

  const validate = () => {
    const errors = {};

    const rawPhone = phoneInputRef.current?.value || form.phone;
    const resolvedPhone =
      phoneItiRef.current && typeof phoneItiRef.current.getNumber === "function"
        ? phoneItiRef.current.getNumber() || rawPhone
        : rawPhone;
    const rawEmergencyPhone =
      emergencyPhoneInputRef.current?.value || form.emergencyContactPhone;
    const resolvedEmergencyPhone =
      emergencyPhoneItiRef.current &&
      typeof emergencyPhoneItiRef.current.getNumber === "function"
        ? emergencyPhoneItiRef.current.getNumber() || rawEmergencyPhone
        : rawEmergencyPhone;
    const phoneMessage = phoneError(resolvedPhone, "Phone");
    if (phoneMessage) errors.phone = phoneMessage;

    const emergencyMessage = phoneError(resolvedEmergencyPhone, "Emergency contact phone");
    if (emergencyMessage) errors.emergencyContactPhone = emergencyMessage;

    const icMessage = icNumberError(form.icNumber, form.country);
    if (icMessage) errors.icNumber = icMessage;

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      return "Please correct the highlighted fields.";
    }

    const requiredFields = [
      "email",
      "title",
      "fullName",
      "badgeName",
      "gender",
      "country",
      "mealPreference",
      "knowAbout",
      "emergencyContactName",
    ];

    const missing = requiredFields.filter((key) => !form[key]);
    if (missing.length > 0) return "Please fill in all required fields.";

    if (form.mealPreference === "Others" && !form.mealPreferenceOther) {
      return "Please specify the dietary restriction.";
    }

    if (eventOptions.length === 0) {
      return "No accompanying events are available yet.";
    }

    const hasSelection = eventOptions.some((option) => selectedEvents[option.key]);
    if (!hasSelection) {
      return "Please select at least one event.";
    }

    return null;
  };

  const handleSave = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const rawPhone = phoneInputRef.current?.value || form.phone;
    const resolvedPhone =
      phoneItiRef.current && typeof phoneItiRef.current.getNumber === "function"
        ? phoneItiRef.current.getNumber() || rawPhone
        : rawPhone;
    const rawEmergencyPhone =
      emergencyPhoneInputRef.current?.value || form.emergencyContactPhone;
    const resolvedEmergencyPhone =
      emergencyPhoneItiRef.current &&
      typeof emergencyPhoneItiRef.current.getNumber === "function"
        ? emergencyPhoneItiRef.current.getNumber() || rawEmergencyPhone
        : rawEmergencyPhone;

    const payload = {
      id: initialPerson?.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      user: {
        email: form.email,
        title: form.title,
        full_name: form.fullName,
        badge_name: form.badgeName,
        gender: form.gender,
        country: form.country,
        ic_number: form.icNumber,
        phone: resolvedPhone,
        attending_reception: selectedEvents.reception ? "Yes" : "No",
        meal_preference: form.mealPreference,
        meal_preference_other: form.mealPreferenceOther,
        know_about: form.knowAbout,
        emergency_contact_name: form.emergencyContactName,
        emergency_contact_phone: resolvedEmergencyPhone,
      },
      events: {
        ticket: selectedEvents.ticket,
        opening: selectedEvents.opening,
        reception: selectedEvents.reception,
        gala: selectedEvents.gala,
      },
    };

    onSavePerson(payload);
    onHide();
  };

  const isEditing = Boolean(initialPerson);

  return (
    <Modal show={show} onHide={closeModal} size="lg" centered className="ticket-modal">
      <Modal.Body style={{ background: "#ebebeb" }}>
        <div className="flex flex-col gap-4 text-sm md:p-10">
          <div>
            <h3>{isEditing ? "Edit Accompanying Person" : "Accompanying Person Registration"}</h3>
            <p className="m-0">
              Fill in login and personal details, then select the events to attend.
            </p>
          </div>
          {error && <div className="alert alert-danger">{error}</div>}

          <div className="p-3 bg-white rounded md:py-4 md:px-6">
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">Title / Salutation*</label>
                <select
                  className="form-select"
                  value={form.title}
                  onChange={(e) => updateField("title", e.target.value)}
                >
                  <option value="">- Select -</option>
                  {titles.map((title) => (
                    <option key={title} value={title}>
                      {title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12">
                <label className="form-label">Full Name*</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.fullName}
                  onChange={(e) => updateField("fullName", e.target.value)}
                />
              </div>
              <div className="col-12">
                <label className="form-label">Name on Badge*</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.badgeName}
                  onChange={(e) => updateField("badgeName", e.target.value)}
                />
              </div>
              <div className="col-12">
                <label className="form-label">Gender*</label>
                <select
                  className="form-select"
                  value={form.gender}
                  onChange={(e) => updateField("gender", e.target.value)}
                >
                  <option value="">- Select -</option>
                  {genders.map((gender) => (
                    <option key={gender} value={gender}>
                      {gender}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12">
                <label className="form-label">Nationality*</label>
                <select
                  className="form-select"
                  value={form.country}
                  onChange={(e) => updateField("country", e.target.value)}
                >
                  <option value="">- Select -</option>
                  {countries.map((country, index) => (
                    <option
                      key={`${country.value || country[1]}-${index}`}
                      value={country.value || country[1]}
                    >
                      {country.label || country[0]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12">
                <label className="form-label">IC / Passport Number*</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Example: 990908121234/ C1234567"
                  value={form.icNumber}
                  onChange={(e) => updateField("icNumber", e.target.value)}
                />
                {fieldErrors.icNumber && (
                  <div className="mt-1 text-danger small">{fieldErrors.icNumber}</div>
                )}
              </div>
              <div className="col-12">
                <label className="form-label">Mobile Number*</label>
                <input
                  type="tel"
                  className="form-control"
                  defaultValue={form.phone}
                  onChange={(e) => updateField("phone", e.target.value)}
                  ref={phoneInputRef}
                  autoComplete="tel"
                />
                {fieldErrors.phone && (
                  <div className="mt-1 text-danger small">{fieldErrors.phone}</div>
                )}
              </div>
              <div className="col-12">
                <label className="form-label">Email*</label>
                <input
                  type="email"
                  className="form-control"
                  value={form.email}
                  onChange={(e) => updateField("email", e.target.value)}
                />
              </div>
              <div className="col-12">
                <label className="form-label">Dietary Restriction*</label>
                <select
                  className="form-select"
                  value={form.mealPreference}
                  onChange={(e) => updateField("mealPreference", e.target.value)}
                >
                  <option value="">- Select -</option>
                  {mealPreferences.map((pref) => (
                    <option key={pref} value={pref}>
                      {pref}
                    </option>
                  ))}
                </select>
              </div>
              {form.mealPreference === "Others" && (
                <div className="col-12">
                  <label className="form-label">Please Specify Dietary Restriction*</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.mealPreferenceOther}
                    onChange={(e) => updateField("mealPreferenceOther", e.target.value)}
                  />
                </div>
              )}
              <div className="col-12">
                <label className="form-label">How Do You Know About This Event?*</label>
                <select
                  className="form-select"
                  value={form.knowAbout}
                  onChange={(e) => updateField("knowAbout", e.target.value)}
                >
                  <option value="">- Select -</option>
                  {knowAbouts.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12">
                <label className="form-label">Emergency Contact Person*</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.emergencyContactName}
                  onChange={(e) => updateField("emergencyContactName", e.target.value)}
                />
              </div>
              <div className="col-12">
                <label className="form-label">Emergency Contact Number*</label>
                <input
                  type="tel"
                  className="form-control"
                  defaultValue={form.emergencyContactPhone}
                  onChange={(e) => updateField("emergencyContactPhone", e.target.value)}
                  ref={emergencyPhoneInputRef}
                  autoComplete="tel"
                />
                {fieldErrors.emergencyContactPhone && (
                  <div className="mt-1 text-danger small">
                    {fieldErrors.emergencyContactPhone}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-3 bg-white rounded md:py-4 md:px-6">
            <h5 className="mb-3">C. Attending Events</h5>
            {eventOptions.length === 0 ? (
              <div className="small text-muted">No accompanying events are available yet.</div>
            ) : (
              <>
                <div className="mb-2 small text-muted">Select at least one event.</div>
                <div className="gap-2 d-flex flex-column">
                  {eventOptions.map((option) => (
                    <label key={option.key} className="gap-2 d-flex align-items-center">
                      <input
                        type="checkbox"
                        checked={selectedEvents[option.key]}
                        disabled={option.soldOut && !selectedEvents[option.key]}
                        onChange={() =>
                          setSelectedEvents((prev) => ({
                            ...prev,
                            [option.key]: !prev[option.key],
                          }))
                        }
                      />
                      <span className={option.soldOut && !selectedEvents[option.key] ? "text-muted" : ""}>
                        {option.label}
                      </span>
                      <div className={option.soldOut && !selectedEvents[option.key] ? "text-red-500" : "fw-bold"}>
                        {option.soldOut && !selectedEvents[option.key]
                          ? "Sold Out"
                          : `(+${formatMoney(option.priceCents, option.currency || currency)})`}
                      </div>
                    </label>
                  ))}
                </div>
                {selectedEventLabels.length > 0 && (
                  <div className="mt-3 small text-muted">
                    Selected: {selectedEventLabels.join(", ")}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex justify-center gap-2">
            <Button className="theme-btn-light" onClick={closeModal}>
              Back
            </Button>
            <Button className="theme-btn" onClick={handleSave}>
              {isEditing ? "Save" : "Add"}
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export { AccompanyingPersonModal };
export default AccompanyingPersonCard;
